import { AlertCircle, X } from "lucide-react";
import { useMemo, useState } from "react";
import type { UseFormSetValue } from "react-hook-form";

import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import type { UseOrganizationsCatalogResult } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaDateField } from "@/shared/components/forms/ColocaDateField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { usePermissionLabel } from "../hooks/usePermissionLabel";
import type { InternalApiKey, InternalApiKeyPermission } from "../types/internalApiKeys.types";
import {
  buildInternalApiKeySchema,
  defaultFormValues,
  maxExpiryDate,
  offersIpWaiver,
  type InternalApiKeyFormValues,
} from "../utils/internalApiKeyForm";
import { InternalApiKeyIpsEditor } from "./InternalApiKeyIpsEditor";

const FORM_ID = "internal-api-key-form";

export interface InternalApiKeyFormDrawerProps {
  open: boolean;
  /** `null` creates a key; a key edits it. */
  apiKey: InternalApiKey | null;
  permissionCatalog: readonly InternalApiKeyPermission[];
  /** The permissions a key may use from any IP when it opts in; empty hides the option. */
  ipOptionalPermissions: readonly InternalApiKeyPermission[];
  maxTtlDays: number;
  catalog: UseOrganizationsCatalogResult;
  /** Resolves true once saved, false when the person backed out of the verification code. */
  onSubmit: (values: InternalApiKeyFormValues) => Promise<boolean>;
  onClose: () => void;
}

export function InternalApiKeyFormDrawer({
  open,
  apiKey,
  permissionCatalog,
  ipOptionalPermissions,
  maxTtlDays,
  catalog,
  onSubmit,
  onClose,
}: InternalApiKeyFormDrawerProps) {
  const { t } = useTranslations("internalApiKeys");
  const { t: tBatches } = useTranslations("payoutBatches");
  const permissionLabel = usePermissionLabel();

  const schema = useMemo(
    () =>
      buildInternalApiKeySchema({
        nameRequired: t("error_name"),
        permissionsRequired: t("error_permissions"),
        organizationsRequired: t("error_organizations"),
        expiryRequired: t("error_expiry"),
        rateLimitInvalid: t("error_rate_limit"),
        tooManyIps: t("error_ips"),
      }),
    [t],
  );
  const form = useZodForm(schema, defaultFormValues(apiKey, maxTtlDays));
  const values = form.watch() as InternalApiKeyFormValues;
  const errors = form.formState.errors;

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Organizations found through the server search are not in the preloaded catalog page.
  const [pickedNames, setPickedNames] = useState<Record<string, string>>({});

  const setField: UseFormSetValue<InternalApiKeyFormValues> = (name, value) =>
    form.setValue(name, value, { shouldValidate: form.formState.isSubmitted, shouldDirty: true });

  const organizationName = (id: string) =>
    catalog.organizations.find((org) => org.id === id)?.name ?? pickedNames[id] ?? id;

  const togglePermission = (permission: InternalApiKeyPermission, checked: boolean) =>
    setField(
      "permissions",
      checked ? [...values.permissions, permission] : values.permissions.filter((p) => p !== permission),
    );

  const submit = async (submitted: InternalApiKeyFormValues) => {
    setSubmitError(null);
    setIsSaving(true);
    try {
      if (await onSubmit(submitted)) onClose();
    } catch (error: unknown) {
      setSubmitError(HttpApiService.getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const maxDate = maxExpiryDate(maxTtlDays);

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={(next) => !next && onClose()}
      size="xl"
      pinLastChild={false}
      title={apiKey ? t("form_title_edit", { name: apiKey.name }) : t("form_title_create")}
      description={t("form_description")}
      footer={
        <div className="flex justify-end gap-2">
          <ColocaButton variant="ghost" onClick={onClose} disabled={isSaving}>
            {t("cancel")}
          </ColocaButton>
          <ColocaButton variant="primary" type="submit" form={FORM_ID} loading={isSaving}>
            {apiKey ? t("form_submit_edit") : t("form_submit_create")}
          </ColocaButton>
        </div>
      }
    >
      <ColocaForm id={FORM_ID} form={form} onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
          <ColocaInputField
            name="name"
            label={t("field_name")}
            placeholder={t("field_name_placeholder")}
            required
          />
          <ColocaInputField name="description" label={t("field_description")} maxLength={500} />
        </div>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">{t("section_permissions")}</h3>
          <label className="flex items-start justify-between gap-4">
            <span>
              <span className="block text-sm text-foreground">{t("all_permissions_label")}</span>
              <span className="block text-xs text-muted-foreground">{t("all_permissions_hint")}</span>
            </span>
            <ColocaSwitch
              checked={values.all_permissions}
              onCheckedChange={(checked) => setField("all_permissions", checked)}
              aria-label={t("all_permissions_label")}
            />
          </label>
          {!values.all_permissions && (
            <div className="grid gap-2 sm:grid-cols-2">
              {permissionCatalog.map((permission) => (
                <ColocaCheckbox
                  key={permission}
                  checked={values.permissions.includes(permission)}
                  onCheckedChange={(checked) => togglePermission(permission, checked)}
                  label={permissionLabel(permission)}
                />
              ))}
            </div>
          )}
          {errors.permissions?.message && (
            <p className="text-xs text-destructive">{String(errors.permissions.message)}</p>
          )}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">{t("section_organizations")}</h3>
          <label className="flex items-start justify-between gap-4">
            <span>
              <span className="block text-sm text-foreground">{t("all_organizations_label")}</span>
              <span className="block text-xs text-muted-foreground">{t("all_organizations_hint")}</span>
            </span>
            <ColocaSwitch
              checked={values.all_organizations}
              onCheckedChange={(checked) => setField("all_organizations", checked)}
              aria-label={t("all_organizations_label")}
            />
          </label>
          {!values.all_organizations && (
            <>
              <OrganizationScopePicker
                id="internal-api-key-organizations"
                mode="required"
                value={null}
                onValueChange={(id, label) => {
                  if (id && !values.organization_ids.includes(id)) {
                    if (label) setPickedNames((names) => ({ ...names, [id]: label }));
                    setField("organization_ids", [...values.organization_ids, id]);
                  }
                }}
                catalog={catalog}
                labels={{
                  label: t("organizations_add"),
                  placeholder: tBatches("org_picker_placeholder"),
                  loading: tBatches("org_picker_loading"),
                  empty: tBatches("org_picker_empty"),
                  searching: tBatches("org_picker_searching"),
                  searchError: tBatches("org_picker_search_error"),
                  catalogError: tBatches("org_picker_catalog_error"),
                  catalogTruncated: tBatches("org_picker_catalog_truncated"),
                  requiredHint: t("organizations_add_hint"),
                  orgHint: t("organizations_add_hint"),
                }}
              />
              {values.organization_ids.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {values.organization_ids.map((id) => (
                    <li
                      key={id}
                      className="flex items-center gap-1 rounded-full border border-border bg-muted/40 py-0.5 pr-1 pl-3 text-sm"
                    >
                      <span className="max-w-56 truncate">{organizationName(id)}</span>
                      <ColocaIconButton
                        icon={X}
                        variant="ghost"
                        size="xs"
                        aria-label={t("organizations_remove", { name: organizationName(id) })}
                        onClick={() =>
                          setField(
                            "organization_ids",
                            values.organization_ids.filter((other) => other !== id),
                          )
                        }
                      />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {errors.organization_ids?.message && (
            <p className="text-xs text-destructive">{String(errors.organization_ids.message)}</p>
          )}
        </section>

        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t("section_ips")}</h3>
            <p className="text-xs text-muted-foreground">{t("ips_hint")}</p>
          </div>
          <InternalApiKeyIpsEditor
            ips={values.allowed_ips}
            onChange={(ips) => setField("allowed_ips", ips)}
          />
          {offersIpWaiver(values, ipOptionalPermissions) && (
            <ColocaCheckbox
              checked={values.allow_low_risk_without_ip}
              onCheckedChange={(checked) => setField("allow_low_risk_without_ip", checked)}
              label={t("allow_without_ip_label")}
              description={t("allow_without_ip_hint")}
            />
          )}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">{t("section_limits")}</h3>
          <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
            <ColocaDateField
              name="expires_on"
              label={t("field_expires_on")}
              description={t("field_expires_hint", { days: maxTtlDays })}
              minDate={tomorrow}
              maxDate={maxDate}
              fromYear={tomorrow.getFullYear()}
              toYear={maxDate.getFullYear()}
            />
            <ColocaInputField
              name="rate_limit_per_minute"
              type="number"
              inputMode="numeric"
              label={t("field_rate_limit")}
              description={t("field_rate_limit_hint")}
            />
          </div>
        </section>

        {submitError && <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />}
      </ColocaForm>
    </ColocaDrawer>
  );
}
